import { isUnauthorizedError, redirectToLogin } from '@/queries/auth-error'
import { entityKeys, type EntityInvalidation } from '@/queries/entity-keys'
import type { InvalidationConfig } from '@/queries/mutation-options'
import { useToast } from '@repo/lui/composables'
import { MutationCache, QueryCache, QueryClient } from '@tanstack/vue-query'

const STALE_TIME_MS = 1000 * 60 * 5 // 5 minutes
const MAX_RETRY_COUNT = 3

function retry(failureCount: number, error: Error): boolean {
  if (isUnauthorizedError(error)) return false
  return error.message === 'Network Error' && failureCount < MAX_RETRY_COUNT
}

function resolveEntityKey(entry: EntityInvalidation): readonly string[] {
  // CA has no per-customer keys, so every entity factory takes no arguments.
  return entityKeys[entry.entity]()
}

async function handleMutationInvalidation(
  client: QueryClient,
  { awaited = [], background = [] }: InvalidationConfig,
): Promise<void> {
  const awaitedKeys = awaited.map(resolveEntityKey)
  const backgroundKeys = background.map(resolveEntityKey)

  if (awaitedKeys.length > 0) {
    await Promise.all(awaitedKeys.map((queryKey) => client.invalidateQueries({ queryKey })))
  }

  for (const queryKey of backgroundKeys) {
    void client.invalidateQueries({ queryKey })
  }
}

const queryCache = new QueryCache({
  onError: (error) => {
    if (isUnauthorizedError(error)) {
      redirectToLogin()
    }
  },
})

const mutationCache = new MutationCache({
  onSuccess: async (_data, _variables, _context, mutation) => {
    const invalidation = mutation.meta?.invalidation
    if (!invalidation) return
    await handleMutationInvalidation(queryClient, invalidation)
  },
})

/**
 * The single QueryClient used by the app and all `useQuery`/`useMutation`
 * call sites. Three cross-cutting behaviours:
 *
 * - `mutationCache.onSuccess` reads `meta.invalidation` (set by
 *   `mutationOptions`) and invalidates the affected entity keys, so
 *   components never have to call `queryClient.invalidateQueries` manually.
 * - `queryCache.onError` redirects to the login page on 401s, as a fallback
 *   for the case where `authenticatedFetch`'s refresh attempt also failed
 *   (see `src/utils/authenticatedFetch.ts`).
 * - `mutations.onError` shows a toast for any `HttpError` (our wrapper for
 *   non-2xx ts-rest responses) so call sites can `await mutateAsync(...)`
 *   without `try/catch`; on rejection, success-only code skips and the user
 *   sees the toast. Mirrors CCA's `defaultOptions.mutations.onError`.
 */
export const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: STALE_TIME_MS,
      retry,
    },
    mutations: {
      onError: (error) => {
        if (isUnauthorizedError(error)) {
          redirectToLogin()
          return
        }
        const toast = useToast()
        toast.error(error.message || 'Something went wrong. Please try again.', {
          closable: true,
          duration: STALE_TIME_MS,
        })
      },
    },
  },
})
